import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import EscolhaDoAlvo from "./EscolhaDoAlvo";
import SeletorDeAtendente from "../SeletorDeAtendente";

// Passar vários atendimentos de uma vez.
//
// Par natural do encerrar em massa: fim de expediente, férias, a fila que
// mudou de dono. Segue a mesma regra da transferência de um — com atendente o
// atendimento fica aberto na mão dele; só para a fila, volta a aguardar.
// Mesma regra do encerrar em massa: com ids, é a seleção da pessoa e a
// escolha do alvo sai da tela.
const TransferirTodosModal = ({ open, onClose, aoConcluir, ticketIds }) => {
	const porSelecao = Boolean(ticketIds?.length);
	const [alvo, setAlvo] = useState("ambos");
	const [contagens, setContagens] = useState(null);
	const [carregando, setCarregando] = useState(false);
	const [salvando, setSalvando] = useState(false);

	const [usuario, setUsuario] = useState(null);
	const [fila, setFila] = useState("");
	const [filas, setFilas] = useState([]);

	const { findAll: buscarFilas } = useQueues();

	useEffect(() => {
		if (!open) return;

		setAlvo("ambos");
		setUsuario(null);
		setFila("");
		setCarregando(true);

		(async () => {
			try {
				// Com seleção a contagem não é usada: o número já é o tamanho do
				// que a pessoa marcou. As filas são sempre necessárias.
				const [contagem, listaDeFilas] = await Promise.all([
					porSelecao
						? Promise.resolve(null)
						: api.get("/tickets/bulk-preview").then(({ data }) => data),
					buscarFilas(),
				]);
				setContagens(contagem);
				setFilas(listaDeFilas || []);
			} catch {
				setContagens(null);
			}
			setCarregando(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const quantos = porSelecao
		? ticketIds.length
		: alvo === "ambos"
			? (contagens?.open || 0) + (contagens?.pending || 0)
			: contagens?.[alvo] || 0;

	const podeTransferir = Boolean(usuario || fila) && quantos > 0;

	const transferir = async () => {
		setSalvando(true);
		try {
			const { data } = await api.put("/tickets/transfer-all", {
				alvo,
				ticketIds: porSelecao ? ticketIds : undefined,
				userId: usuario?.id,
				queueId: fila || undefined,
			});

			toast.success(
				data?.transferidos
					? `${data.transferidos} atendimento(s) transferido(s).`
					: "Nenhum atendimento para transferir.",
			);

			if (data?.falhas) {
				toast.warning(
					`${data.falhas} atendimento(s) não puderam ser transferidos.`,
				);
			}

			if (aoConcluir) aoConcluir(data);
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{porSelecao
							? "Transferir os atendimentos selecionados"
							: "Transferir vários atendimentos"}
					</DialogTitle>
					<DialogDescription>
						Escolha um atendente, um departamento, ou os dois. Só o departamento devolve os
						atendimentos para Aguardando.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{porSelecao ? (
						<p className="rounded-lg border bg-muted/40 p-2.5 text-sm">
							<strong className="tabular-nums">{ticketIds.length}</strong>{" "}
							conversa{ticketIds.length > 1 ? "s" : ""} selecionada
							{ticketIds.length > 1 ? "s" : ""} na lista.
						</p>
					) : (
						<EscolhaDoAlvo
							valor={alvo}
							aoMudar={setAlvo}
							contagens={contagens}
							carregando={carregando}
						/>
					)}

					<div className="space-y-1.5">
						<Label>Para quem</Label>
						<SeletorDeAtendente
							aberto={open}
							valor={usuario}
							aoEscolher={setUsuario}
							placeholder="Nenhum (só muda o departamento)"
							rotuloLimpar="Tirar o atendente"
						/>
					</div>

					<div className="space-y-1.5">
						<Label>Departamento (opcional)</Label>
						<Select value={fila} onValueChange={setFila}>
							<SelectTrigger>
								<SelectValue placeholder="Manter o departamento atual" />
							</SelectTrigger>
							<SelectContent>
								{filas.map((f) => (
									<SelectItem key={f.id} value={String(f.id)}>
										{f.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<p className="text-xs text-muted-foreground">
						Cada transferência fica registrada na conversa, como já acontece na
						transferência de um só.
					</p>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={salvando}>
						Cancelar
					</Button>
					<Button onClick={transferir} disabled={salvando || !podeTransferir}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						{quantos ? `Transferir ${quantos}` : "Nada para transferir"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default TransferirTodosModal;
